import { Form } from 'antd';
import { snakeCase } from 'lodash';
import { pipe } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId } from 'api/models';
import { FieldOptionBase } from 'api/tasks';
import { UserComponents } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { getControlledSelectBasedEditor } from 'components/organisms/field-editors/components/controlled-select-based-editor';
import { OptionGroup } from 'components/organisms/field-editors/components/controlled-select-based-editor-popover';
import {
  HoverableUserTag,
  TriggerContent,
} from 'components/organisms/field-editors/components/controlled-select-based-editor-trigger-content';
import { map, orElse } from 'core/maybe';
import {
  AssigneePickerFilter,
  useAssigneeOptions,
} from 'hooks/use-assignee-options';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import { byLabel } from 'utils/comparators/by-label';
import { AssigneeField, AssigneeValue, TeamId, UserId } from '../fields';
import { AssigneeFieldFilter } from '../filters/assignee';
import {
  isOptional,
  SelectFilterOperationType,
  SelectFilterOperationTypes,
} from '../filters/select';
import { Select } from './components';
import { useProjectIdFromUrl } from './use-user-options';

interface Props {
  filter: AssigneeFieldFilter;
  onChange: (filter: AssigneeFieldFilter) => void;
}

const options = (field: AssigneeField): AssigneeFieldFilter[] =>
  SelectFilterOperationTypes.filter(
    (x) => !field.metaData?.required || !isOptional(x),
  ).map(
    (type) =>
      ({
        field,
        type,
      } as any as AssigneeFieldFilter),
  );

interface SelectProps {
  field: AssigneeField;
  value: AssigneeValue[];
  filterType:
    | SelectFilterOperationType.IsNoneOf
    | SelectFilterOperationType.IsOneOf
    | SelectFilterOperationType.Is
    | SelectFilterOperationType.IsNot;
  onChange: (latest: AssigneeValue[]) => void;
}

const toAssignee = ({
  value,
}: FieldOptionBase<string>): AssigneeValue | undefined =>
  value.startsWith('user_')
    ? UserId(parseInt(value.replace('user_', ''), 10))
    : TeamId(parseInt(value.replace('team_', ''), 10));

const Editor = getControlledSelectBasedEditor<true, string>(
  'assignee-criteria',
  'criteria.assignee.select',
);

export const AssigneeSelect: FC<SelectProps> = ({
  field,
  value,
  filterType,
  onChange,
}) => {
  const maybeProjectId = useProjectIdFromUrl();

  const projectId = useMemo(
    () => ProjectId(maybeProjectId[1]!),
    [maybeProjectId],
  );

  const assigneePickerOptionsAll = useAssigneeOptions({
    projectId,
    filter: AssigneePickerFilter.NativePartnerUsersAndTeams,
  });

  const assigneePickerOptionsByProjectId = useAssigneeOptions({
    projectId,
    filter: AssigneePickerFilter.InProjectUsersAndTeams,
  });

  const assigneeOptions: OptionGroup<string>[] = pipe(
    () => maybeProjectId,
    map(() => assigneePickerOptionsByProjectId),
    orElse<OptionGroup<string>[]>(assigneePickerOptionsAll),
  )();

  const flattened = assigneeOptions.reduce(
    (prev, x) => prev.concat(x.options),
    [] as FieldOptionBase<string>[],
  );

  const [latest, setLatest] = useState(value);

  const onClose = useCallback(() => {
    onChange(latest);
  }, [latest, onChange]);

  useEffect(() => {
    setLatest(value);
  }, [value, setLatest]);

  const assignees = latest
    .map((x) => x.join('_'))
    .map((x) => flattened.find((o) => o.value === x)!)
    .filter(Boolean)
    .sort(byLabel)
    .map((x) => x as FieldOptionBase<string>);
  const items = (value ?? [])
    .map((x) => x.join('_'))
    .map((x) => flattened.find((o) => o.value === x)!)
    .filter(Boolean)
    .sort(byLabel)
    .map((x) => x as FieldOptionBase<string>)
    .map((x) => <HoverableUserTag {...x} />);

  const title = useTranslation('task_list.filters.messages.editor_title', {
    field: field.fieldLabel,
    operation: useTranslation(
      `task_list.filters.${snakeCase(filterType)}` as TranslationKey,
    ),
  });

  return (
    <Editor
      title={title}
      components={{ ...UserComponents, MenuList: VirtualizedMenuList }}
      isMulti
      options={assigneeOptions}
      value={assignees}
      onChange={(latest_options?: FieldOptionBase<string>[]) =>
        setLatest(
          (latest_options ?? [])
            .sort(byLabel)
            .map((x) => toAssignee(x as any)!)
            .filter(Boolean),
        )
      }
      triggerContent={<TriggerContent items={items} />}
      onClose={onClose}
      type="user"
      grouped
    />
  );
};

export const AssigneeCriteria: FC<Props> = ({ filter, onChange }) => (
  <>
    <Form.Item>
      <Select
        disabled={filter == null}
        value={filter?.type}
        onChange={(selected) =>
          onChange({
            ...filter,
            type: selected,
          } as any)
        }
      >
        {(filter?.field == null ? [] : options(filter.field)).map((option) => (
          <Select.Option key={option.type} value={option.type}>
            <FormattedMessage
              id={`task_list.filters.${snakeCase(
                SelectFilterOperationType[option.type],
              )}`}
            />
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
    {filter != null &&
      (filter.type === SelectFilterOperationType.Is ||
        filter.type === SelectFilterOperationType.IsNot ||
        filter.type === SelectFilterOperationType.IsOneOf ||
        filter.type === SelectFilterOperationType.IsNoneOf) && (
        <Form.Item>
          <AssigneeSelect
            field={filter.field}
            value={filter.value ?? []}
            filterType={filter.type}
            onChange={(selected) =>
              onChange({
                ...filter,
                value: selected as any,
              })
            }
          />
        </Form.Item>
      )}
  </>
);
